/**
=========================================================
* Trust Cargo - the customers in one container
=========================================================

* Every customer with orders in the container, each once, with what they have
* in it and what they are charged for it. The columns are in the office's
* order: code, name, VAT name, total CBM, total KG, price, extra, amount,
* phone, address.
*
* Price and extra are typed straight into the table and saved the moment the
* box is left (or Enter is pressed) - one field at a time, so saving a price
* never blanks an extra typed a moment before. Amount is price x total CBM +
* extra, worked out as the numbers are typed rather than after the save, so
* the table never shows a stale amount.
*
* The customer's agreed CBM price, when there is one, shows as a hint in an
* empty price box. It is not a price until somebody types it.
*/

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

const API = "https://trust-cargo.co/anas_backend/api/v2";

export const COLUMNS = [
  "Customer code",
  "Customer name",
  "VAT name",
  "Total CBM",
  "Total KG",
  // the customer's cartons in this container - the third measurement
  "Total CTN",
  "Price",
  "Extra",
  "Amount",
  "Phone number",
  "Address",
];

/** A number, or null for a box left empty - never NaN. */
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const show = (v, digits = 3) =>
  v === null || v === undefined
    ? "-"
    : Number(v).toLocaleString("en-US", { maximumFractionDigits: digits });

/** price x CBM + extra; nothing when neither has been typed. */
const amountOf = (price, cbm, extra) => {
  const p = num(price);
  const e = num(extra);

  if (p === null && e === null) return null;

  return Math.round(((p || 0) * (num(cbm) || 0) + (e || 0)) * 100) / 100;
};

const cell = { fontSize: 13, whiteSpace: "nowrap" };

function ContainerCustomers({ containerId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  // what is being typed, by "customerId.field", until it is saved
  const [drafts, setDrafts] = useState({});
  const [rowErrors, setRowErrors] = useState({});

  useEffect(() => {
    let alive = true;

    setData(null);
    setError("");

    fetch(`${API}/containers/${containerId}/customers`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError(`Could not load the customers - ${e.message}`);
      });

    return () => {
      alive = false;
    };
  }, [containerId]);

  const rows = (data && data.customers) || [];
  const keyOf = (row, field) => `${row.customer_id}.${field}`;
  const valueOf = (row, field) => {
    const k = keyOf(row, field);
    if (k in drafts) return drafts[k];
    return row[field] === null || row[field] === undefined ? "" : String(row[field]);
  };

  const dropDraft = (k) =>
    setDrafts((d) => {
      const next = { ...d };
      delete next[k];
      return next;
    });

  /** Save one field of one customer, if it changed. */
  const save = async (row, field) => {
    const k = keyOf(row, field);
    if (!(k in drafts)) return;

    const typed = String(drafts[k]).trim();
    const value = typed === "" ? null : num(typed);

    if (typed !== "" && value === null) {
      setRowErrors((e) => ({ ...e, [row.customer_id]: `${field} has to be a number` }));
      return;
    }

    if (value === row[field]) {
      dropDraft(k);
      return;
    }

    setRowErrors((e) => ({ ...e, [row.customer_id]: "" }));

    try {
      const res = await axios.put(`${API}/containers/${containerId}/customers/${row.customer_id}`, {
        [field]: value,
      });
      const saved = (res && res.data) || {};

      setData((d) => ({
        ...d,
        customers: d.customers.map((c) =>
          c.customer_id === row.customer_id
            ? {
                ...c,
                price: saved.price !== undefined ? saved.price : c.price,
                extra: saved.extra !== undefined ? saved.extra : c.extra,
              }
            : c
        ),
      }));
      dropDraft(k);
    } catch (e) {
      setRowErrors((errs) => ({
        ...errs,
        [row.customer_id]: e?.response?.data?.message || "Could not save",
      }));
    }
  };

  // the totals follow what is on screen, typed-but-unsaved numbers included
  const totals = rows.reduce(
    (t, r) => ({
      cbm: t.cbm + (num(r.total_cbm) || 0),
      kg: t.kg + (num(r.total_kg) || 0),
      ctn: t.ctn + (num(r.total_ctn) || 0),
      // whether anybody's cartons have been counted at all - a column of
      // dashes should not add up to a confident 0
      counted: t.counted || num(r.total_ctn) !== null,
      extra: t.extra + (num(valueOf(r, "extra")) || 0),
      amount: t.amount + (amountOf(valueOf(r, "price"), r.total_cbm, valueOf(r, "extra")) || 0),
    }),
    { cbm: 0, kg: 0, ctn: 0, counted: false, extra: 0, amount: 0 }
  );

  const box = (row, field, extraProps) => (
    <TextField
      size="small"
      type="number"
      value={valueOf(row, field)}
      onChange={(e) => setDrafts((d) => ({ ...d, [keyOf(row, field)]: e.target.value }))}
      onBlur={() => save(row, field)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.target.blur();
      }}
      sx={{ width: 110 }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...extraProps}
    />
  );

  let body;

  if (error) {
    body = (
      <SuiTypography sx={{ fontSize: 14 }} color="error">
        {error}
      </SuiTypography>
    );
  } else if (!data) {
    body = <SuiTypography sx={{ fontSize: 14, opacity: 0.7 }}>Loading customers...</SuiTypography>;
  } else if (rows.length === 0) {
    body = (
      <SuiTypography sx={{ fontSize: 14, opacity: 0.7 }}>
        No orders in this container yet
      </SuiTypography>
    );
  } else {
    body = (
      <SuiBox sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 1200, "& .MuiTableCell-root": { px: 1, py: 0.75 } }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell key={c} component="th" align="center">
                  {c}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((r) => [
              <TableRow key={r.customer_id}>
                <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                  {r.code || "-"}
                </TableCell>
                <TableCell align="center" sx={cell} dir="auto">
                  {r.name || "-"}
                </TableCell>
                <TableCell align="center" sx={cell} dir="auto">
                  {r.vat_name || "-"}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {show(r.total_cbm)}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {show(r.total_kg, 2)}
                </TableCell>
                {/* "-" until somebody has counted them: no cartons counted
                    is not the same as none */}
                <TableCell align="center" sx={cell}>
                  {show(r.total_ctn, 0)}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {box(r, "price", {
                    placeholder:
                      r.default_price !== null && r.default_price !== undefined
                        ? `agreed ${r.default_price}`
                        : "",
                    inputProps: { min: 0, step: "any", "aria-label": `Price for ${r.code}` },
                  })}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {box(r, "extra", {
                    inputProps: { step: "any", "aria-label": `Extra for ${r.code}` },
                  })}
                </TableCell>
                <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                  {show(amountOf(valueOf(r, "price"), r.total_cbm, valueOf(r, "extra")), 2)}
                </TableCell>
                <TableCell align="center" sx={cell}>
                  {r.phone || "-"}
                </TableCell>
                <TableCell align="center" sx={cell} dir="auto">
                  {r.address || "-"}
                </TableCell>
              </TableRow>,

              rowErrors[r.customer_id] ? (
                <TableRow key={`${r.customer_id}-error`}>
                  <TableCell colSpan={COLUMNS.length} sx={{ py: 0.5 }}>
                    <SuiTypography sx={{ fontSize: 12.5 }} color="error">
                      {`${r.code}: ${rowErrors[r.customer_id]}`}
                    </SuiTypography>
                  </TableCell>
                </TableRow>
              ) : null,
            ])}

            <TableRow>
              <TableCell colSpan={3} align="right" sx={{ ...cell, fontWeight: 800 }}>
                Total
              </TableCell>
              <TableCell align="center" sx={{ ...cell, fontWeight: 800 }}>
                {show(totals.cbm)}
              </TableCell>
              <TableCell align="center" sx={{ ...cell, fontWeight: 800 }}>
                {show(totals.kg, 2)}
              </TableCell>
              <TableCell align="center" sx={{ ...cell, fontWeight: 800 }}>
                {/* a total only of what was counted - nothing counted is "-" */}
                {show(totals.counted ? totals.ctn : null, 0)}
              </TableCell>
              <TableCell />
              <TableCell align="center" sx={{ ...cell, fontWeight: 800 }}>
                {show(totals.extra, 2)}
              </TableCell>
              <TableCell align="center" sx={{ ...cell, fontWeight: 800 }}>
                {show(totals.amount, 2)}
              </TableCell>
              <TableCell colSpan={2} />
            </TableRow>
          </TableBody>
        </Table>
      </SuiBox>
    );
  }

  return (
    <Card>
      <SuiBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={3}
        pt={2.5}
        pb={1.5}
      >
        <SuiTypography variant="h6">Customers in this container</SuiTypography>
        {rows.length > 0 && (
          <SuiTypography sx={{ fontSize: 13, opacity: 0.7 }}>
            {`${rows.length} customer${rows.length === 1 ? "" : "s"}`}
          </SuiTypography>
        )}
      </SuiBox>
      <SuiBox px={3} pb={3}>
        {body}
      </SuiBox>
    </Card>
  );
}

ContainerCustomers.propTypes = {
  containerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ContainerCustomers;
